import type { UserRole } from '@/models/types';

export function canSyncData(role: UserRole): boolean {
  return role === 'Admin';
}

export function canManageProjects(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

/**
 * Basic role-based check for app management.
 * Use canManageAppsWithDelegation() for delegated admin support.
 */
export function canManageApps(role: UserRole): boolean {
  return role === 'Admin';
}

/**
 * Can this specific user manage the app catalog?
 * Admins always can. Also any user whose email is in the OC_AppAdmins delegated list.
 * Pass the delegated admin emails list (fetched from SharePoint OC_AppAdmins).
 */
export function canManageAppsDelegated(
  role: UserRole,
  currentUserEmail: string,
  delegatedAdminEmails: string[],
): boolean {
  if (role === 'Admin') return true;
  const lower = currentUserEmail.toLowerCase();
  return delegatedAdminEmails.map((e) => e.toLowerCase()).includes(lower);
}

/**
 * Returns true if user can manage the app catalog, considering delegated app admins.
 * @param role User's Entra role
 * @param userEmail The signed-in user's email
 * @param delegatedAdminEmails List of emails from OC_AppAdmins that have been delegated
 * @param appId Optional — if passed, check if they have access to this specific app
 * @param delegatedAdmins Optional — full AppAdmin list to check per-app permissions
 */
export function canManageAppsWithDelegation(
  role: UserRole,
  userEmail: string,
  delegatedAdminEmails: string[],
  appId?: string,
  delegatedAdmins?: Array<{ Email: string; AllowedApps: string }>,
): boolean {
  if (role === 'Admin') return true;
  if (!userEmail) return false;
  const lower = userEmail.toLowerCase();
  if (!delegatedAdminEmails.map((e) => e.toLowerCase()).includes(lower)) {
    return false;
  }
  // If app-specific check, validate the admin has this app in their AllowedApps
  if (appId && delegatedAdmins) {
    const entry = delegatedAdmins.find(
      (a) => a.Email.toLowerCase() === lower,
    );
    if (!entry) return false;
    const allowed = (entry.AllowedApps ?? '').trim();
    if (allowed === '*' || allowed === '') return true; // wildcard or unset = all
    try {
      const ids = JSON.parse(allowed);
      return Array.isArray(ids) && ids.includes(appId);
    } catch {
      return false;
    }
  }
  return true;
}

export function canManageAccess(role: UserRole): boolean {
  return role === 'Admin';
}

export function canApproveExpenses(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

export function canViewAllExpenses(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

export function canViewAllMembers(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

export function canManageRoles(role: UserRole): boolean {
  return role === 'Admin';
}

export function canEmailReports(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

/**
 * Can this user edit allocations for a specific project?
 * Admins + ROs can edit any project.
 * Project owners (email matches OwnerEmail) can edit their own projects — enables PM self-service.
 */
export function canEditProjectAllocations(
  role: UserRole,
  currentUserEmail: string,
  projectOwnerEmail: string,
): boolean {
  if (role === 'Admin' || role === 'RO') return true;
  if (
    currentUserEmail &&
    projectOwnerEmail &&
    currentUserEmail.toLowerCase() === projectOwnerEmail.toLowerCase()
  ) {
    return true;
  }
  return false;
}

export function canViewCosts(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

export function canViewAllAllocations(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

export function canViewAuditLog(role: UserRole): boolean {
  return role === 'Admin';
}

export function canViewSnapshots(role: UserRole): boolean {
  return role === 'Admin' || role === 'RO';
}

/**
 * Client-side row filter for sensitive lists.
 * Returns a predicate function to filter items by role.
 * Admin/RO see all; Member sees only own data.
 */
export function filterByRole<T>(
  role: UserRole,
  currentEmail: string,
  getOwnerEmail: (item: T) => string,
): (item: T) => boolean {
  if (role === 'Admin' || role === 'RO') return () => true;
  const lowerEmail = currentEmail.toLowerCase();
  return (item: T) => getOwnerEmail(item).toLowerCase() === lowerEmail;
}
